import { message } from '@/components/UI';
import { FormatMoney } from '@/utils';
import { i18nCCYMap, i18nMap } from '@/constants';
import { formatLocalTime } from '@/utils/timeConversionTool';

export const EuropeSites = ['eu', 'uk', 'de', 'fr', 'it', 'es'];
const dateFormatMap: Record<string, string> = {
    us: 'MM/DD/YYYY',
    ca: 'MM/DD/YYYY',
    jp: 'YYYY/MM/DD',
};

const dateTimeFormatMap: Record<string, string> = {
    us: 'MM/DD/YYYY HH:mm:ss',
    ca: 'MM/DD/YYYY HH:mm:ss',
    jp: 'YYYY/MM/DD HH:mm:ss',
};

export const copyText = (text: string, t?: any, locale?: string) => {
    if (navigator?.clipboard && window?.isSecureContext && navigator?.permissions) {
        window.navigator.clipboard.writeText(text);
        if (locale === 'kr') {
            message.success('링크 복사하였습니다');
        } else {
            t && message.success(t('my.site_account_profile_save_success'));
        }
    } else {
        const textArea = document.createElement('textarea');
        textArea.value = text;
        textArea.style.position = 'absolute';
        textArea.style.opacity = '0';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.select();
        new Promise(() => {
            const copyStatus = document.execCommand('copy');
            if (!copyStatus) {
                // message.error()
            }
            textArea.remove();
            if (locale === 'kr') {
                message.success('링크 복사하였습니다');
            } else {
                t && message.success(t('my.site_account_profile_save_success'));
            }
        });
    }
};

// 给coupon添加Notes
export const getNotes = (coupon: any, t: any, locale: string, timezoneList: any) => {
    const noteList: string[] = [t('rules.site_rules'), t('rules.order_rule')];
    // 不找零
    if (coupon?.prerequisite !== 'PRICES' && coupon?.discountType === 'fixed_amount') {
        noteList.push(t('rules.purchas_rule'));
    }
    if (coupon?.prerequisite === 'PRICES' && coupon?.discountType === 'fixed_amount') {
        const couponPrice = parseInt(coupon?.amount);
        const priceRange = parseInt(coupon.prerequisiteRange);
        if (priceRange < couponPrice) {
            noteList.push(t('rules.purchas_rule'));
        }
    }
    if (coupon?.goodId) noteList.push(t('rules.product_rules', { product: coupon?.goodTitle || '' }));

    // 最低金额
    if (coupon?.prerequisite === 'PRICES') {
        const price = parseInt(coupon.prerequisiteRange);
        const currencySymbol = coupon?.currencySymbol || '';
        const prerequisiteRange = t('rules.minimum_price', { price: currencySymbol + FormatMoney(price) });
        noteList.push(prerequisiteRange);
    }

    // 最低数量
    if (coupon?.prerequisite === 'QUANTITY') {
        const num = parseInt(coupon.prerequisiteRange);
        const prerequisiteRange = t('rules.minimum_num', { num });
        noteList.push(prerequisiteRange);
    }

    // 过期天数
    const limitDay = Math.floor((coupon?.expiredDay || 0) / 60 / 24);
    if (coupon?.expiredFlag && coupon?.expiredType === 'DAY') {
        noteList.push(t('rules.days_rules', { days: limitDay }));
    }
    // 时间 for 福利中心
    if (coupon?.couponId && coupon?.endTime && coupon?.expiredType === 'TIME') {
        const formater = dateTimeFormatMap[locale as string] || dateTimeFormatMap['us'];
        const startTime = formatLocalTime(coupon?.startTime, timezoneList, formater);
        const endTime = formatLocalTime(coupon?.endTime, timezoneList, formater);
        noteList.push(t('rules.days_rules2', { data: `${startTime}-${endTime}` }));
    }

    // 时间 for 优惠券
    if (coupon?.coupon && coupon?.endTime) {
        const formater = dateTimeFormatMap[locale as string] || dateTimeFormatMap['us'];
        const startTime = formatLocalTime(coupon?.startTime, timezoneList, formater);
        const endTime = formatLocalTime(coupon?.endTime, timezoneList, formater);
        noteList.push(t('rules.days_rules2', { data: `${startTime}-${endTime}` }));
    }

    // 时间 for 优惠券规则
    if (!(coupon?.coupon || coupon?.couponId) && coupon?.expiredFlag && coupon?.validEndTime && coupon?.expiredType === 'TIME') {
        const formater = dateTimeFormatMap[locale as string] || dateTimeFormatMap['us'];
        const startTime = formatLocalTime(coupon?.validStartTime, timezoneList, formater);
        const endTime = formatLocalTime(coupon?.validEndTime, timezoneList, formater);
        noteList.push(t('rules.days_rules2', { data: `${startTime}-${endTime}` }));
    }

    // campaign限时型时间
    if (!coupon?.discountId && coupon?.campaignStartTime && coupon?.campaignEndTime) {
        const formater = dateFormatMap[locale as string] || dateFormatMap['us'];
        const startTime = formatLocalTime(coupon?.campaignStartTime, timezoneList, formater);
        const endTime = formatLocalTime(coupon?.campaignEndTime, timezoneList, formater);
        noteList.push(t('rules.campaign_time_limit_text', { startTime, endTime }));
    }
    if (coupon?.extRuleDesc) {
        noteList.push(coupon?.extRuleDesc);
    }
    return noteList;
};

export const formatPrice = (locale: string, num: number, isSymbol = true) => {
    let price: string;

    // two decimal config
    const stay2FloatOptions = {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    };
    const symbol = isSymbol ? i18nCCYMap[locale] : '';

    // different site different config
    if (locale === 'za') {
        price = ` ${new Intl.NumberFormat('en-us', stay2FloatOptions).format(num)}`;
        // if (!/([,|.]\d{2})$/.test(price)) {
        //     price += `.00`;
        // }
    } else if (['es', 'fr', 'it', 'de', 'eu'].includes(locale)) {
        let mergeOptions = {};
        if (!['fr'].includes(locale)) {
            mergeOptions = Object.assign({}, stay2FloatOptions);
        }
        // price = price.replaceAll(',', '.').replace(/(.*)\./, '$1,');
        price = new Intl.NumberFormat('en-de', mergeOptions).format(num);
    } else {
        let mergeOptions = {};
        if (!['kr', 'jp'].includes(locale)) {
            // price += `.00`;
            mergeOptions = Object.assign({}, stay2FloatOptions);
        }
        price = new Intl.NumberFormat(i18nMap[locale], mergeOptions).format(num);
    }

    if (['de', 'fr', 'it', 'es'].includes(locale)) {
        price += `${symbol}`;
    } else {
        price = `${symbol}${price}`;
    }

    return price;
};
